// frontend/src/Components/SyncTo/helpers/silenceDetector.js
// Phase 30 — stateful silence detector. Feed it RMS samples; it reports
// first-2s-silence / 30s-rolling-warn / 60s-auto-stop transitions.
// Reused by: useSyncToRecorder's rAF loop (SYNC-04).
// Thresholds are tunable constants per Assumption A2 (30-RESEARCH.md).

export const RMS_SILENCE_THRESHOLD = 0.02;   // ≈ -34 dB, above typical mic noise floor (A2 tuning — 0.005 sat under hardware hiss)
export const FIRST_SILENCE_MS = 2000;        // SYNC-04 first-2s mic-check
export const ROLLING_WARN_MS  = 30_000;      // SYNC-04 30s rolling warn
export const AUTO_STOP_MS     = 60_000;      // SYNC-04 60s auto-stop (D-05 download)

/**
 * createSilenceDetector — returns { feed, reset }.
 *
 * feed(rmsSample, nowMs) is called once per rAF tick (or whenever RMS is sampled).
 * Returns an object with three edge-triggered booleans:
 *   { firstSilenceFired, rollingWarnFired, autoStopFired }.
 * Each becomes true exactly once per underlying condition; caller is expected
 * to act on them (dialog, toast, stop) and NOT to re-fire after reset().
 */
export function createSilenceDetector({
  threshold = RMS_SILENCE_THRESHOLD,
  firstMs = FIRST_SILENCE_MS,
  rollingWarnMs = ROLLING_WARN_MS,
  autoStopMs = AUTO_STOP_MS,
} = {}) {
  let startedAt = null;            // rAF start time (first feed call)
  let silentSince = null;          // wall-clock at which current silent run began
  let firstSilenceFired = false;   // edge flag: first-2s condition
  let rollingWarnFired = false;    // edge flag: 30s rolling condition
  let autoStopFired = false;       // edge flag: 60s auto-stop condition

  function feed(rmsSample, nowMs) {
    if (startedAt === null) startedAt = nowMs;
    const elapsedSinceStart = nowMs - startedAt;
    const isSilent = rmsSample < threshold;

    if (isSilent) {
      if (silentSince === null) silentSince = nowMs;
    } else {
      silentSince = null;
    }

    // First-2s mic-check: fires only in the opening window, once, if still silent at firstMs.
    let ev_first = false;
    if (!firstSilenceFired && elapsedSinceStart >= firstMs && silentSince !== null && silentSince <= startedAt + 50) {
      firstSilenceFired = true;
      ev_first = true;
    }

    // Rolling 30s warn — fires after a contiguous silent run reaches rollingWarnMs.
    let ev_warn = false;
    if (!rollingWarnFired && silentSince !== null && nowMs - silentSince >= rollingWarnMs) {
      rollingWarnFired = true;
      ev_warn = true;
    }

    // 60s auto-stop — fires after a contiguous silent run reaches autoStopMs.
    let ev_stop = false;
    if (!autoStopFired && silentSince !== null && nowMs - silentSince >= autoStopMs) {
      autoStopFired = true;
      ev_stop = true;
    }

    return { firstSilenceFired: ev_first, rollingWarnFired: ev_warn, autoStopFired: ev_stop };
  }

  function reset() {
    startedAt = null;
    silentSince = null;
    firstSilenceFired = false;
    rollingWarnFired = false;
    autoStopFired = false;
  }

  return { feed, reset };
}
